import React, { useState, useContext, useEffect } from 'react';
import axios from '../../axiosConfig';
import OwnerLayout from './OwnerLayout';
import { OwnerContext } from '../../context/OwnerContext';
import '../../styles/OwnerAccount.css';

const OwnerAccount = () => {
    const { owner, refreshOwnerData } = useContext(OwnerContext);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: {
            country: '',
            city: '',
            postalCode: '',
            street: '',
            buildingNumber: '',
            apartmentNumber: ''
        }
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [containerStyle, setContainerStyle] = useState({});

    // Initialize form data when owner data is loaded
    useEffect(() => {
        if (owner) {
            setFormData({
                firstName: owner.firstName || '',
                lastName: owner.lastName || '',
                email: owner.email || '',
                phone: owner.phone || '',
                address: {
                    country: owner.address?.country || '',
                    city: owner.address?.city || '',
                    postalCode: owner.address?.postalCode || '',
                    street: owner.address?.street || '',
                    buildingNumber: owner.address?.buildingNumber || '',
                    apartmentNumber: owner.address?.apartmentNumber || ''
                }
            });
        }
    }, [owner]);

    // Handle responsive styling based on screen width
    useEffect(() => {
        const updateContainerStyle = () => {
            const width = window.innerWidth;

            if (width >= 800 && width <= 850) {
                // iPad Air and similar devices
                setContainerStyle({
                    marginLeft: '140px',
                    marginRight: '30px',
                    width: 'calc(100% - 170px)',
                    paddingLeft: '20px'
                });
            } else if (width >= 601 && width <= 1024) {
                // Other tablets
                setContainerStyle({
                    marginLeft: '120px',
                    marginRight: '50px',
                    width: 'calc(100% - 170px)',
                    paddingLeft: '10px'
                });
            } else if (width <= 600) {
                // Mobile
                setContainerStyle({
                    marginLeft: '10px',
                    marginRight: '10px',
                    width: 'calc(100% - 20px)',
                    padding: '0'
                });
            } else {
                // Desktop
                setContainerStyle({});
            }
        };

        updateContainerStyle();
        window.addEventListener('resize', updateContainerStyle);

        return () => window.removeEventListener('resize', updateContainerStyle);
    }, []);

    if (!owner) return <OwnerLayout><div className="owner-account-tab__loading">Ładowanie...</div></OwnerLayout>;

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            address: {
                ...prevData.address,
                [name]: value
            }
        }));
    };

    const handleEditToggle = () => {
        if (isEditing) {
            // Reset form data to original owner data when canceling
            setFormData({
                firstName: owner.firstName || '',
                lastName: owner.lastName || '',
                email: owner.email || '',
                phone: owner.phone || '',
                address: {
                    country: owner.address?.country || '',
                    city: owner.address?.city || '',
                    postalCode: owner.address?.postalCode || '',
                    street: owner.address?.street || '',
                    buildingNumber: owner.address?.buildingNumber || '',
                    apartmentNumber: owner.address?.apartmentNumber || ''
                }
            });
            setError('');
            setSuccessMessage('');
        }
        setIsEditing(!isEditing);
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        // Ensure address object exists and has the right structure
        const updateData = {
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            email: formData.email || '',
            phone: formData.phone || '',
            address: {
                country: formData.address?.country || '',
                city: formData.address?.city || '',
                postalCode: formData.address?.postalCode || '',
                street: formData.address?.street || '',
                buildingNumber: formData.address?.buildingNumber || '',
                apartmentNumber: formData.address?.apartmentNumber || ''
            }
        };

        try {
            const response = await axios.put('/api/v1/owner/update', updateData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            // Update the form data with the response
            if (response.data) {
                setFormData(response.data);
            }

            // Refresh the owner data in context
            await refreshOwnerData();

            setIsEditing(false);
            setSuccessMessage('Zmiany zapisane pomyślnie!');
        } catch (err) {
            console.error('Error updating owner:', err);
            console.error('Error response:', err.response?.data);
            setError('Nie udało się zapisać zmian.');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleChangePasswordSubmit = async () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');

        if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            setError('Nowe hasła nie są zgodne.');
            setLoading(false);
            return;
        }

        try {
            await axios.put('/api/v1/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
            });

            setSuccessMessage('Hasło zostało zmienione pomyślnie!');
            setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
            setIsChangingPassword(false);
        } catch (err) {
            setError('Nie udało się zmienić hasła. Sprawdź poprawność starego hasła.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <OwnerLayout>
            <div className="owner-account-container" style={containerStyle}>
                <div className="owner-account-tab">
                    {/* Personal Information Section */}
                    <div className="owner-account-tab__section">
                        <h3 className="owner-account-tab__section-title">Informacje osobiste</h3>

                        <div className="owner-account-tab__row owner-account-tab__row--personal-info">
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Imię</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName || ''}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    placeholder="Imię"
                                />
                            </div>
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Nazwisko</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName || ''}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    placeholder="Nazwisko"
                                />
                            </div>
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">E-mail</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="email"
                                    name="email"
                                    value={formData.email || ''}
                                    disabled
                                    placeholder="E-mail"
                                />
                            </div>
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Telefon</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="phone"
                                    value={formData.phone || ''}
                                    onChange={handleChange}
                                    disabled={!isEditing}
                                    placeholder="Telefon"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Address Section */}
                    <div className="owner-account-tab__section">
                        <h3 className="owner-account-tab__section-title">Adres</h3>

                        {/* First row: Country, Postal Code, City */}
                        <div className="owner-account-tab__address-row-1">
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Kraj</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="country"
                                    value={formData.address?.country || ''}
                                    onChange={handleAddressChange}
                                    disabled={!isEditing}
                                    placeholder="Kraj"
                                />
                            </div>
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Kod pocztowy</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="postalCode"
                                    value={formData.address?.postalCode || ''}
                                    onChange={handleAddressChange}
                                    disabled={!isEditing}
                                    placeholder="Kod pocztowy"
                                />
                            </div>
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Miasto</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="city"
                                    value={formData.address?.city || ''}
                                    onChange={handleAddressChange}
                                    disabled={!isEditing}
                                    placeholder="Miasto"
                                />
                            </div>
                        </div>

                        {/* Second row: Street, Building Number, Apartment Number */}
                        <div className="owner-account-tab__address-row-2">
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Ulica</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="street"
                                    value={formData.address?.street || ''}
                                    onChange={handleAddressChange}
                                    disabled={!isEditing}
                                    placeholder="Ulica"
                                />
                            </div>
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Numer budynku</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="buildingNumber"
                                    value={formData.address?.buildingNumber || ''}
                                    onChange={handleAddressChange}
                                    disabled={!isEditing}
                                    placeholder="Numer budynku"
                                />
                            </div>
                            <div className="owner-account-tab__field-group">
                                <label className="owner-account-tab__label">Numer mieszkania</label>
                                <input
                                    className="owner-account-tab__input"
                                    type="text"
                                    name="apartmentNumber"
                                    value={formData.address?.apartmentNumber || ''}
                                    onChange={handleAddressChange}
                                    disabled={!isEditing}
                                    placeholder="Numer mieszkania"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    {error && <div className="owner-account-tab__error-message">{error}</div>}
                    {successMessage && <div className="owner-account-tab__success-message">{successMessage}</div>}

                    {/* Action Buttons - Main actions and password change in one row */}
                    <div className="owner-account-tab__button-group">
                        <button className="owner-account-tab__btn owner-account-tab__btn--secondary" onClick={handleEditToggle}>
                            {isEditing ? "Anuluj" : "Edytuj"}
                        </button>
                        {isEditing && (
                            <button className="owner-account-tab__btn owner-account-tab__btn--primary" onClick={handleSubmit} disabled={loading}>
                                {loading ? "Zapisywanie..." : "Zapisz"}
                            </button>
                        )}
                        {!isChangingPassword && (
                            <button className="owner-account-tab__btn owner-account-tab__btn--outline" onClick={() => setIsChangingPassword(true)}>
                                Zmień hasło
                            </button>
                        )}
                    </div>

                    {/* Password Change Section */}
                    {isChangingPassword && (
                        <div className="owner-account-tab__section owner-account-tab__password-section">
                            <h3 className="owner-account-tab__section-title">Zmiana hasła</h3>
                            <div className="owner-account-tab__row">
                                <div className="owner-account-tab__field-group">
                                    <label className="owner-account-tab__label">Stare hasło</label>
                                    <input
                                        className="owner-account-tab__input"
                                        type="password"
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Stare hasło"
                                    />
                                </div>
                                <div className="owner-account-tab__field-group">
                                    <label className="owner-account-tab__label">Nowe hasło</label>
                                    <input
                                        className="owner-account-tab__input"
                                        type="password"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Nowe hasło"
                                    />
                                </div>
                                <div className="owner-account-tab__field-group">
                                    <label className="owner-account-tab__label">Powtórz nowe hasło</label>
                                    <input
                                        className="owner-account-tab__input"
                                        type="password"
                                        name="confirmNewPassword"
                                        value={passwordData.confirmNewPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Powtórz nowe hasło"
                                    />
                                </div>
                            </div>
                            <div className="owner-account-tab__button-group">
                                <button className="owner-account-tab__btn owner-account-tab__btn--secondary" onClick={() => setIsChangingPassword(false)}>
                                    Anuluj
                                </button>
                                <button className="owner-account-tab__btn owner-account-tab__btn--primary" onClick={handleChangePasswordSubmit} disabled={loading}>
                                    {loading ? "Zapisywanie..." : "Zapisz hasło"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </OwnerLayout>
    );
};

export default OwnerAccount;